// src/utils/jwt.ts
import { DecodedJwt, JwtPayload } from '../types/api';

/**
 * Decodes a Base64 URL encoded string.
 * Handles URL-specific characters ('-', '_') and decodes percent-encoded characters.
 *
 * @param base64Url - The Base64 URL encoded string to decode.
 * @returns The decoded string.
 * @throws {Error} If the Base64 string is invalid.
 */
function base64UrlDecode(base64Url: string): string {
    try {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        // Pad the string if necessary, as '=' padding characters might be omitted in URL-safe Base64
        const paddedBase64 = base64 + '==='.slice(0, (4 - base64.length % 4) % 4);
        const binaryString = atob(paddedBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        // Use TextDecoder for robust UTF-8 decoding
        return new TextDecoder().decode(bytes);
    } catch (e) {
        console.error("Failed to decode Base64 URL string:", e);
        throw new Error("Invalid Base64 URL string");
    }
}

/**
 * Decodes a JSON Web Token (JWT) and returns its header and payload.
 * Performs basic validation to ensure the token has the expected structure.
 *
 * @param token - The JWT string to decode.
 * @returns An object containing the decoded header and payload. Returns empty objects if token is invalid or null/undefined.
 */
export function decodeJwt(token: string | null | undefined): DecodedJwt {
    if (!token) {
        console.warn("decodeJwt called with null or undefined token.");
        return { header: {}, payload: {} as JwtPayload };
    }

    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error("Invalid JWT structure: Token does not contain 3 parts.");
            return { header: {}, payload: {} as JwtPayload };
        }

        const [header, payload] = parts;
        const decodedHeader = JSON.parse(base64UrlDecode(header));
        const decodedPayload = JSON.parse(base64UrlDecode(payload)) as JwtPayload; // Assert payload type

        // Basic check for essential payload fields (optional but recommended)
        if (typeof decodedPayload.sub === 'undefined' || typeof decodedPayload.is_temp === 'undefined') {
             console.warn("JWT payload missing expected fields (sub, is_temp).");
             // Decide if this should be an error or just a warning
        }


        return { header: decodedHeader, payload: decodedPayload };
    } catch (error) {
        console.error("Failed to decode JWT:", error);
        return { header: {}, payload: {} as JwtPayload }; // Return empty objects on error
    }
}
